import React from "react";
import { ReferralEntry, referralEntryStatus } from "../../features/admin/models/ReferralEntry";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const formatDate = (date: Date): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);

const formatMoney = (minorUnits: number, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);

const statusBadge: Record<string, { label: string; className: string }> = {
  attributed: {
    label: "Signed up",
    className: "bg-secondary-90 dark:bg-secondary-20 text-secondary-20 dark:text-secondary-90",
  },
  converted: {
    label: "Converted",
    className: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
  },
  paid: {
    label: "Paid out",
    className: "bg-grey-90 dark:bg-primary-20 text-grey-30 dark:text-grey-70",
  },
};

interface ReferralEntriesTableProps {
  referrals: ReferralEntry[];
}

/** A partner's referred accounts with their attributed → converted → paid state. */
const ReferralEntriesTable: React.FC<ReferralEntriesTableProps> = ({ referrals }) => {
  if (referrals.length === 0) {
    return (
      <div className="with-border rounded-2xl p-8 text-center text-grey-40 dark:text-grey-70">
        No referrals yet — share the partner's link to get started.
      </div>
    );
  }

  return (
    <div className="space-y-2 lg:space-y-4">
      {referrals.map((entry) => {
        const badge = statusBadge[referralEntryStatus(entry)];
        const name = `${entry.firstName} ${entry.lastName}`.trim();
        return (
          <div
            key={entry.id}
            className="with-border flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4 lg:p-6"
          >
            <div className="min-w-0">
              <p className="truncate font-semibold text-grey-10 dark:text-white">
                {name || entry.email || "Deleted account"}
              </p>
              <p className="mt-1 text-xs text-grey-50 dark:text-grey-60">
                Signed up {formatDate(entry.attributedAt)}
                {entry.convertedAt && ` · converted ${formatDate(entry.convertedAt)}`}
                {entry.paidAt && ` · paid ${formatDate(entry.paidAt)}`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${badge.className}`}>
                {badge.label}
              </span>
              <span className="text-sm font-semibold text-grey-20 dark:text-grey-80">
                {formatMoney(entry.bountyAmountMinorUnits, entry.bountyCurrency, getDisplayLocale())}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ReferralEntriesTable;
